import React, { useCallback, useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Button,
  Card,
  Icon,
  styles as ui,
  useConfirm,
} from "stories/Admin";
import {
  describeMissing,
  isRunning,
  NorwegianReport,
  NorwegianSyncStatus,
  progressPercent,
  statusSummary,
} from "./norwegianSync";
import styles from "./QuranPage.module.scss";

const POLL_MS = 2000;

interface Props {
  /** Kalles med ny rapport (til merkene i surah-listen). */
  onReport?: (report: NorwegianReport) => void;
  /** Kalles når en synk er ferdig, så valgt surah kan lastes på nytt. */
  onFinished?: () => void;
}

/**
 * «Hent norske oversettelser»: fyller inn norsk der den mangler eller bare er
 * plassholderen «kommer snart» (Maududi fra leskoranen.no). Kjører i
 * bakgrunnen på serveren; panelet viser fremdrift og oppsummering.
 */
const NorwegianSyncPanel: React.FC<Props> = ({ onReport, onFinished }) => {
  const { execute } = useFetch();
  const confirm = useConfirm();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const onReportRef = useRef(onReport);
  onReportRef.current = onReport;
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const [report, setReport] = useState<NorwegianReport | null>(null);
  const [status, setStatus] = useState<NorwegianSyncStatus | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    const result = (await executeRef.current(
      "/api/v1/admin/quran/norwegian/report",
      "GET",
    )) as NorwegianReport | null;
    if (result) {
      setReport(result);
      onReportRef.current?.(result);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    const result = (await executeRef.current(
      "/api/v1/admin/quran/norwegian/sync",
      "GET",
    )) as NorwegianSyncStatus | null;
    if (result) setStatus(result);
    return result;
  }, []);

  useEffect(() => {
    fetchReport();
    fetchStatus();
  }, [fetchReport, fetchStatus]);

  // Følg med mens synken kjører; hent rapporten på nytt når den er ferdig.
  const running = isRunning(status);
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(async () => {
      const next = await fetchStatus();
      if (next && !isRunning(next)) {
        await fetchReport();
        onFinishedRef.current?.();
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, fetchStatus, fetchReport]);

  const start = async () => {
    if (
      !(await confirm({
        title: "Hente norske oversettelser?",
        message:
          "Norsk oversettelse (Maududi, leskoranen.no) hentes for alle vers som mangler den eller bare har «kommer snart». Vers som allerede har norsk tekst endres ikke. Det kjører i bakgrunnen og kan ta noen minutter.",
        confirmLabel: "Hent norske oversettelser",
      }))
    ) {
      return;
    }
    setStarting(true);
    setError(null);
    const result = (await executeRef.current(
      "/api/v1/admin/quran/norwegian/sync",
      "POST",
    )) as NorwegianSyncStatus | null;
    if (result) {
      setStatus(result);
      if (!isRunning(result)) {
        await fetchReport();
        onFinishedRef.current?.();
      }
    } else {
      setError("Kunne ikke starte hentingen av norske oversettelser.");
    }
    setStarting(false);
  };

  const missing = report?.missingAyahs ?? 0;
  const showResult =
    status && (status.state === "done" || status.state === "failed");

  return (
    <Card
      title="Norsk oversettelse"
      subtitle={
        report
          ? missing === 0
            ? `Alle ${report.totalAyahs.toLocaleString("nb-NO")} vers har norsk oversettelse.`
            : `${missing} vers i ${report.surahs.length} surah-er mangler norsk` +
              (report.placeholderAyahs
                ? ` (${report.placeholderAyahs} med «kommer snart»/plassholder).`
                : ".")
          : "Sjekker …"
      }
      actions={
        <Button
          onClick={start}
          disabled={running || starting}
          loading={running || starting}
          icon={<Icon name="download" size={16} strokeWidth={2} />}
        >
          {running ? "Henter norsk …" : "Hent norske oversettelser"}
        </Button>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}

      {status && running && (
        <div className={styles.syncProgress} aria-live="polite">
          <div
            className={styles.syncBar}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercent(status)}
          >
            <span style={{ width: `${progressPercent(status)}%` }} />
          </div>
          <span className={ui.help}>{statusSummary(status)}</span>
        </div>
      )}

      {showResult && status && (
        <Alert
          tone={
            status.state === "failed"
              ? "error"
              : (status.missingAfter ?? 0) > 0 || status.failedSurahs.length
                ? "warn"
                : "ok"
          }
        >
          {statusSummary(status)}
        </Alert>
      )}

      {showResult && status && status.failedSurahs.length > 0 && (
        <div className={styles.syncList}>
          <b>Feilet</b>
          <ul>
            {status.failedSurahs.map((s) => (
              <li key={s.surahNumber}>
                {s.surahNumber} {s.name}: {s.message ?? "ukjent feil"}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report && report.surahs.length > 0 && !running && (
        <div className={styles.syncList}>
          <b>Mangler fortsatt norsk</b>
          <ul>
            {report.surahs.map((s) => (
              <li key={s.surahNumber}>{describeMissing(s)}</li>
            ))}
          </ul>
        </div>
      )}

      <span className={ui.help}>
        API-et sender aldri «kommer snart» til appene: mangler norsk, er feltet
        tomt (null), og appene kan vise engelsk/urdu i stedet.
      </span>
    </Card>
  );
};

export default NorwegianSyncPanel;
